import moment from 'moment';
import { buildFilterApiParams } from '@intelligence/v3/filterControls/apiParams';
import { FILTER_TYPE } from '@intelligence/v3/filterControls/filterTypes';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { getCountryByCode, getRegionByCode } from '@intelligence/v3/utils/region';
import { isEmpty, normalizeCode } from '@intelligence/v3/utils/common';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import { getValidNumber } from '@intelligence/v3/utils/number';
import { getCountryFlag } from '@home/_config/countries';
import { getAlineaCalibrationSourceMetrics } from '@intelligence/components/ExportTable/utils';
import { getTrueDataMetricText } from '@intelligence/components/PublicTrueData/utils/utils';
import {
  ALINEA_ALL_PLATFORM_ICONS,
  ALINEA_CALIBRATION_METRICS,
  ALINEA_CALIBRATION_MAX_DOWNLOAD_ROWS,
  ALINEA_PLATFORM_NAME_MAP,
  ALINEA_REGION_NAME_MAP,
  DEFAULT_FILTER_VALUES,
  PUBLISHER_CLASS_OPTIONS,
  SOURCE_QUALITY_OPTIONS,
} from './constants';

import type { IFilterGroup, IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';
import type { IGenreSource } from '@intelligence/components/GenreSelect/types';
import type { RecordItemType } from '@intelligence/components/SearchRecent/type';
import type {
  IAlineaCalibrationFilterValues,
  IAlineaCalibrationListParams,
  IAlineaCalibrationPaging,
  IAlineaCalibrationRecord,
  IAlineaCalibrationSortState,
} from './types';

export const ALINEA_FILTER_GROUPS: IFilterGroup[] = [
  {
    key: 'popular',
    title: 'dbi_filter_group_popular',
    filterKeys: ['rangeTime', 'metrics', 'region', 'genre', 'games', 'sourceQuality', 'releaseTime'],
  },
  {
    key: 'company',
    title: 'dbi_filter_group_company',
    filterKeys: ['publisher', 'publisherClass', 'publisherRegion', 'developer'],
  },
];

export const getAlineaCalibrationFilterItems = (params: {
  genreOptions: IRecord;
  regionOptions: IRecord[];
  publisherClassOptions: IRecord[];
  publisherRegionOptions: IRegion[];
}): IFilterItemConfig[] => {
  const { genreOptions, regionOptions, publisherClassOptions, publisherRegionOptions } = params;
  const companyControlProps = {
    entityType: 'combine',
    source: 'alinea',
    dataSources: ['alinea'],
    metric: 'alinea_calibration__revenue',
    regionType: 'market',
    regions: ['global'],
    granularity: 'daily',
    start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    end_date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
  };

  return [
    {
      type: FILTER_TYPE.RANGE_PICKER,
      field: 'rangeTime',
      controlProps: {
        showSelectType: true,
        typeList: ['date', 'week', 'month'],
        showShortcut: true,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.MULTIPLE_SELECT,
      field: 'metrics',
      label: 'dbi_label_metrics',
      apiParamsFieldMapResolver: () => {
        return { value: 'metrics' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'metrics' };
      },
      controlProps: {
        title: 'dbi_label_metrics',
        hasAllCheck: true,
        options: ALINEA_CALIBRATION_METRICS.map((item) => {
          return {
            text: item.label,
            textCN: item.labelCN,
            value: item.value,
          };
        }),
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.MULTIPLE_SELECT,
      field: 'region',
      label: 'dbi_region',
      apiParamsFieldMapResolver: () => {
        return { value: 'markets' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'markets' };
      },
      controlProps: {
        title: 'dbi_region',
        hasAllCheck: true,
        options: regionOptions,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.GENRE_SELECT,
      field: 'genre',
      label: 'dbi_genre',
      apiParamsFieldMapResolver: () => {
        return {
          ieggGenreReq: 'iegg_genre_req',
        };
      },
      controlProps: {
        sources: [GENRE_SOURCE_MAP.IEGG],
        genresOptionsData: genreOptions,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.GAME_SELECT,
      field: 'games',
      label: 'dbi_games',
      apiParamsFieldMapResolver: () => {
        return {
          keywords: 'search_by_name',
          value: 'filter_ids',
        };
      },
      controlProps: {
        entityKey: 'id',
        isStudio: '',
        hasShortCut: false,
        params: {
          entity_type: 'combine',
          game_type: 'current_game',
        },
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.MULTIPLE_SELECT,
      field: 'sourceQuality',
      label: 'dbi_source_quality',
      apiParamsFieldMapResolver: () => {
        return { value: 'source_quality' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'sourceQuality' };
      },
      controlProps: {
        title: 'dbi_source_quality',
        hasAllCheck: true,
        searchable: false,
        options: SOURCE_QUALITY_OPTIONS,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.RANGE_PICKER,
      field: 'releaseTime',
      apiParamsFieldMapResolver: () => {
        return {
          granularity: 'release_granularity',
          startDate: 'start_release_time',
          endDate: 'end_release_time',
        };
      },
      urlParamsFieldMapResolver: () => {
        return {
          granularity: 'releaseGranularity',
          startDate: 'releaseStartDate',
          endDate: 'releaseEndDate',
        };
      },
      controlProps: {
        showSelectType: false,
        typeList: ['date'],
        showShortcut: true,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.COMPANY_SELECT,
      field: 'publisher',
      label: 'dbi_publisher',
      apiParamsFieldMapResolver: () => {
        return { value: 'company' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'publisher' };
      },
      controlProps: {
        ...companyControlProps,
        title: 'dbi_publisher',
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.PUBLISHER_CLASS_SELECT,
      field: 'publisherClass',
      label: 'dbi_publisher_class',
      apiParamsFieldMapResolver: () => {
        return { value: 'publisher_class' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'publisherClass' };
      },
      controlProps: {
        title: 'dbi_publisher_class',
        options: publisherClassOptions,
        AWidth: 440,
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.REGION_SELECT,
      field: 'publisherRegion',
      label: 'dbi_publisher_region',
      apiParamsFieldMapResolver: () => {
        return { markets: 'publisher_region' };
      },
      urlParamsFieldMapResolver: () => {
        return { markets: 'publisherRegions' };
      },
      controlProps: {
        title: 'dbi_publisher_region',
        options: publisherRegionOptions,
        entityType: 'combine',
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.COMPANY_SELECT,
      field: 'developer',
      label: 'dbi_developer',
      apiParamsFieldMapResolver: () => {
        return { value: 'developer' };
      },
      urlParamsFieldMapResolver: () => {
        return { value: 'developer' };
      },
      controlProps: {
        ...companyControlProps,
        title: 'dbi_developer',
      },
      inherit: ['all'],
    },
  ];
};

const getAlineaRegionNameConfig = (code: string): { text: string; textCN: string } | undefined => {
  return ALINEA_REGION_NAME_MAP[String(code || '').toLowerCase()];
};

export const getAlineaOptionText = (options: IRecord[], value?: string, locale?: string, emptyText = '-'): string => {
  if (!value) {
    return emptyText;
  }

  const normalizedValue = value.toLowerCase();
  const option = options.find((item) => {
    return String(item?.value).toLowerCase() === normalizedValue;
  });
  if (!option) {
    const regionText = getAlineaRegionOptionText(value, locale);
    if (regionText) {
      return regionText;
    }

    // 详情接口可能返回国家码或动态地区码，不能只依赖 Alinea 固定项映射。
    const regionOptionNameConfig = getRegionOptionNameConfig(value, options as IRegion[]);
    if (regionOptionNameConfig.text !== value || regionOptionNameConfig.textCN !== value) {
      return locale === 'zh-CN' ? regionOptionNameConfig.textCN : regionOptionNameConfig.text;
    }

    return value;
  }

  if (locale === 'zh-CN') {
    return option?.textCN || option?.text || value;
  }

  return option?.text || option?.textCN || value;
};

export const getAlineaRegionOptionText = (code?: string, locale?: string): string => {
  const regionNameConfig = getAlineaRegionNameConfig(String(code || ''));
  if (!regionNameConfig) {
    return '';
  }

  if (locale === 'zh-CN') {
    return regionNameConfig.textCN;
  }

  return regionNameConfig.text;
};

const getRegionByCodeOrName = (code: string, regionsCountries: IRegion[]): IRegion | null => {
  const region = getRegionByCode(code, regionsCountries);
  if (region) {
    return region;
  }

  const normalizedCode = normalizeCode(code);
  const regions = Array.isArray(regionsCountries) ? regionsCountries : [];

  return (
    regions.find((item) => {
      return normalizeCode(item?.area_name_en) === normalizedCode;
    }) || null
  );
};

const getRegionOptionNameConfig = (code: string, regionsCountries: IRegion[]): { text: string; textCN: string } => {
  const country = getCountryByCode(code, regionsCountries);
  if (country) {
    const text = country?.country_name_en || code;
    const textCN = country?.country_name_ch || text;

    return {
      text,
      textCN,
    };
  }

  const region = getRegionByCodeOrName(code, regionsCountries);
  if (region) {
    const text = region?.area_name_en || code;
    const textCN = region?.area_name_ch || text;

    return {
      text,
      textCN,
    };
  }

  return {
    text: code,
    textCN: code,
  };
};

export const formatRegionCodesToSelectOptions = (codes: string[], regionsCountries: IRegion[] = []): IRecord[] => {
  const validCodes = codes.filter((code) => {
    return !!code;
  });
  const sortedCodes = [
    ...validCodes.filter((code) => {
      return code.toLowerCase() === 'global';
    }),
    ...validCodes.filter((code) => {
      return code.toLowerCase() !== 'global';
    }),
  ];

  return sortedCodes.map((code) => {
    const regionNameConfig = getAlineaRegionNameConfig(code);
    if (regionNameConfig) {
      return {
        value: code,
        text: regionNameConfig.text,
        textCN: regionNameConfig.textCN,
      };
    }

    const regionNameConfigByCode = getRegionOptionNameConfig(code, regionsCountries);

    return {
      value: code,
      text: regionNameConfigByCode.text,
      textCN: regionNameConfigByCode.textCN,
    };
  });
};

export const getAlineaSourceQualityText = (sourceQuality?: string, locale?: string, emptyText = '-'): string => {
  return getAlineaOptionText(SOURCE_QUALITY_OPTIONS, sourceQuality, locale, emptyText);
};

export const getAlineaPublisherClassText = (publisherClass?: string, locale?: string, emptyText = '-'): string => {
  return getAlineaOptionText(PUBLISHER_CLASS_OPTIONS, publisherClass, locale, emptyText);
};

export const getAlineaGenreText = (params: {
  genreValue?: string;
  genreOptionsMap?: IRecord;
  genreSource?: IGenreSource;
  locale?: string;
  emptyText?: string;
}): string => {
  const genreValue = String(params?.genreValue || '');
  if (!genreValue) {
    return params?.emptyText || '';
  }

  const genreOptionsMap = params?.genreOptionsMap;
  if (!genreOptionsMap) {
    return genreValue;
  }

  return genreValue
    .split('|')
    .map((value) => {
      return getGenreNameI18n({
        locale: params?.locale,
        genreOptionsMap,
        genreSource: params?.genreSource || GENRE_SOURCE_MAP.IEGG,
        genreValue: value.trim(),
      });
    })
    .join('|');
};

export const getAlineaGenreTextFromItem = (params: {
  item: IRecord;
  key: string;
  genreOptionsMap?: IRecord;
  genreSource?: IGenreSource;
  locale?: string;
  emptyText?: string;
}): string => {
  const genreValue = String(params?.item?.[params?.key] || '');

  return getAlineaGenreText({
    genreValue,
    genreOptionsMap: params?.genreOptionsMap,
    genreSource: params?.genreSource,
    locale: params?.locale,
    emptyText: params?.emptyText,
  });
};

export const getAlineaMetricItem = (item: IRecord): IRecord => {
  const metricList = Array.isArray(item?.metric_list) ? item.metric_list : [];
  const metricItem = metricList.find((metric) => {
    return !!metric?.title?.startsWith('alinea_calibration__');
  });

  return metricItem || {};
};

export const getAlineaMetricText = (item: IRecord, locale?: string, emptyText = '-'): string => {
  const metricItem = getAlineaMetricItem(item);
  const metricValue = metricItem?.title || item?.metric || item?.metric_name || '';
  const metricText = getMetricLabel(metricValue, locale);
  if (metricText !== '-' && metricText !== metricValue) {
    return metricText;
  }

  return metricText || emptyText;
};

export const getAlineaDateValue = (item: IRecord): string => {
  return getAlineaMetricItem(item)?.latest_time || item?.date || '';
};

export const getAlineaValue = (item: IRecord): number | string => {
  return getAlineaMetricItem(item)?.value ?? item?.value ?? item?.metric_value ?? '';
};

export const formatAlineaValue = (value: number | string): string => {
  const text = String(value).trim();
  if (!text || Number.isNaN(Number(text))) {
    return '-';
  }

  const normalizedText = Number(text).toLocaleString('fullwide', {
    useGrouping: false,
    maximumFractionDigits: 20,
  });
  const [integerPart, decimalPart] = normalizedText.split('.');
  const sign = integerPart.startsWith('-') ? '-' : '';
  const unsignedInteger = integerPart.replace('-', '');
  const formattedInteger = unsignedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart ? `${sign}${formattedInteger}.${decimalPart}` : `${sign}${formattedInteger}`;
};

/**
 * 解析首行平台图标列表。后端返回 platform: ['all'] 时展开为四大平台。
 */
export const getAlineaPlatformIconKeys = (platforms?: string[]): string[] => {
  if (!Array.isArray(platforms) || !platforms.length) {
    return [];
  }

  const normalized = platforms
    .filter(Boolean)
    .map((platform) => String(platform).toLowerCase());
  if (normalized.includes('all')) {
    return [...ALINEA_ALL_PLATFORM_ICONS];
  }

  return normalized;
};

export const getAlineaPlatformText = (item: IRecord, emptyText = '-'): string => {
  const platforms = item?.platform;
  if (!Array.isArray(platforms)) {
    return emptyText;
  }

  const platformNames = platforms.filter(Boolean).map((platform) => {
    const platformText = String(platform);
    return ALINEA_PLATFORM_NAME_MAP[platformText.toLowerCase()] || platformText;
  });

  return platformNames.join(', ') || emptyText;
};

export const getAlineaRegionCodes = (regionValue: unknown): string[] => {
  if (Array.isArray(regionValue)) {
    return regionValue.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
  }

  if (typeof regionValue === 'string' && regionValue) {
    return regionValue
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

export const getAlineaRegionText = (
  item: IRecord,
  regionOptions: IRecord[],
  locale?: string,
  emptyText = '-',
): string => {
  const regionCodes = getAlineaRegionCodes(item?.alinea_calibration_region);
  if (!regionCodes.length) {
    return emptyText;
  }

  return regionCodes
    .map((code) => {
      return getAlineaOptionText(regionOptions, code, locale, emptyText);
    })
    .join(', ');
};

export const getAlineaPublisherClassOption = (publisherClass: string): IRecord => {
  const option = PUBLISHER_CLASS_OPTIONS.find((item) => {
    return item.value === publisherClass;
  });

  return {
    value: publisherClass,
    text: option?.text || publisherClass,
    textCN: option?.textCN || option?.text || publisherClass,
  };
};

const normalizeApiParams = (params: IRecord): IRecord => {
  const nextParams = { ...params };

  if (!isEmpty(nextParams.filter)) {
    Object.assign(nextParams, nextParams.filter);
    delete nextParams.filter;
  }

  delete nextParams.release_granularity;
  return nextParams;
};

export const buildAlineaCalibrationApiParams = (
  items: IFilterItemConfig[],
  filterValues: IAlineaCalibrationFilterValues,
): IRecord => {
  return normalizeApiParams(buildFilterApiParams(items, filterValues as IFilterValues));
};

const getArrayParam = (value?: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value;
};

const getPublisherRegionParam = (value?: unknown): string[] => {
  const regions = getArrayParam(value);
  if (regions.length === 1 && regions[0] === 'global') {
    return [];
  }

  return regions;
};

const getRegionParam = (value: unknown, regionOptions?: IRecord[]): string[] => {
  const regions = getArrayParam(value);
  if (!isEmpty(regions)) {
    return regions;
  }

  return (regionOptions || [])
    .map((item) => {
      return item?.value;
    })
    .filter((item) => {
      return !!item;
    });
};

export const buildAlineaCalibrationListParams = (params: {
  items: IFilterItemConfig[];
  filterValues: IAlineaCalibrationFilterValues;
  regionOptions?: IRecord[];
  paging: IAlineaCalibrationPaging;
  sortState: IAlineaCalibrationSortState;
  keyword?: string;
  unifiedIds?: string[];
}): IAlineaCalibrationListParams => {
  const filterParams = buildAlineaCalibrationApiParams(params.items, params.filterValues);
  const { markets } = filterParams;
  const gameSearchText = filterParams.search_by_name || filterParams.keywords || '';
  const searchText = params.keyword?.trim() || gameSearchText;
  let metrics = getArrayParam(filterParams.metrics);
  if (isEmpty(metrics)) {
    metrics = ALINEA_CALIBRATION_METRICS.map((item) => {
      return item.value;
    });
  }
  let sourceQuality = filterParams.source_quality;
  if (isEmpty(sourceQuality)) {
    sourceQuality = SOURCE_QUALITY_OPTIONS.map((item) => {
      return item.value;
    });
  }

  const nextParams: IAlineaCalibrationListParams = {
    b_alinea_calibration: 1,
    entity_type: 'combine',
    region_type: 'market',
    regions: getRegionParam(markets, params.regionOptions),
    genre_source: GENRE_SOURCE_MAP.IEGG,
    iegg_genre_req: filterParams.iegg_genre_req || [],
    source_quality: sourceQuality,
    source_metrics: getAlineaCalibrationSourceMetrics(metrics),
    date_type: filterParams.granularity || 'daily',
    page: params.paging.page,
    page_size: params.paging.page_size,
    start_date: filterParams.start_date || '',
    end_date: filterParams.end_date || '',
    start_release_time: filterParams.start_release_time || '',
    end_release_time: filterParams.end_release_time || '',
    search_by_name: searchText,
    unified_ids: params.unifiedIds || [],
    filter_ids: filterParams.filter_ids || [],
    company: getArrayParam(filterParams.company),
    publisher_class: getArrayParam(filterParams.publisher_class),
    publisher_region: getPublisherRegionParam(filterParams.publisher_region),
    developer: getArrayParam(filterParams.developer),
    order_metric: params.sortState.order_metric,
    order: params.sortState.order,
  };

  return nextParams;
};

export const getRecordsFromResponse = (res?: IRecord): IAlineaCalibrationRecord[] => {
  const list = res?.data?.rank_au_list || [];
  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((item, index) => {
    return {
      ...item,
      rank: item?.rank || index + 1,
    };
  });
};

export const transformAlineaSearchRecords = (res?: IRecord): RecordItemType[] => {
  const seenGameIds = new Set<string>();

  return getRecordsFromResponse(res)
    .map((item) => {
      return {
        id: item?.unified_id || '',
        imgSrc: item?.cover || '',
        lineOneStr: item?.name || '',
        lineTwoImg: getCountryFlag(item?.publisher_addr || ''),
        lineTwoImgName: item?.publisher_addr,
        lineTwoStr: item?.publisher || '',
        imageType: 'combine',
      };
    })
    .filter((item) => {
      if (!item.id || seenGameIds.has(item.id)) {
        return false;
      }

      seenGameIds.add(item.id);
      return true;
    });
};

export const getTotalFromResponse = (res?: IRecord): number => {
  return Number(res?.data?.total || 0);
};

export const getDownloadTotalFromResponse = (res?: IRecord): number => {
  const gameTotal = getValidNumber(res?.data?.game_total);
  if (gameTotal !== null) {
    return gameTotal;
  }

  return getTotalFromResponse(res);
};

export const getFilterValuesWithReqDate = (
  filterValues: IAlineaCalibrationFilterValues,
  req?: IRecord,
): IAlineaCalibrationFilterValues => {
  const startDate = req?.start_date || '';
  const endDate = req?.end_date || '';
  if (!startDate || !endDate) {
    return filterValues;
  }

  return {
    ...filterValues,
    rangeTime: {
      granularity: req?.date_type || filterValues?.rangeTime?.granularity || 'daily',
      startDate,
      endDate,
    },
  };
};

export const hasSelectedTime = (filterValues?: IAlineaCalibrationFilterValues): boolean => {
  return !!filterValues?.rangeTime?.startDate && !!filterValues?.rangeTime?.endDate;
};

export const buildAlineaCalibrationDownloadParams = (params: {
  items: IFilterItemConfig[];
  filterValues: IAlineaCalibrationFilterValues;
  regionOptions?: IRecord[];
  regionsCountries?: IRegion[];
  genreOptionsMap?: IRecord;
  sortState: IAlineaCalibrationSortState;
  keyword?: string;
  unifiedIds?: string[];
}): IRecord => {
  const listParams = buildAlineaCalibrationListParams({
    ...params,
    paging: {
      page: 1,
      page_size: 100,
    },
  });
  const downloadFileName = `Top Charts_PC&Console Games_Alinea_Analytics_True Data_${listParams.start_date}_${listParams.end_date}`;

  return {
    ...listParams,
    region_type: 'market',
    file_format: 0,
    company_show: 0,
    genre_show: 0,
    release_date_show: 0,
    regionOptions: params.regionOptions || [],
    regionsCountries: params.regionsCountries || [],
    genreOptionsMap: params.genreOptionsMap || {},
    new_style_donwload_file_name: downloadFileName,
    use_sse_page: {
      max_size: ALINEA_CALIBRATION_MAX_DOWNLOAD_ROWS,
    },
  };
};

export const getMetricLabel = (metric?: string, locale?: string): string => {
  return getTrueDataMetricText(metric, { source: 'alinea', locale });
};

const getAllRegionValues = (regionOptions?: IRecord[]): string[] => {
  return (regionOptions || [])
    .map((item) => {
      return item?.value;
    })
    .filter((item) => {
      return !!item;
    });
};

export const getFilterValuesWithDefaultRegions = (
  filterValues: IAlineaCalibrationFilterValues,
  regionOptions?: IRecord[],
): IAlineaCalibrationFilterValues => {
  if (!isEmpty(filterValues?.region)) {
    return filterValues;
  }

  return {
    ...filterValues,
    region: getAllRegionValues(regionOptions),
  };
};

export const getDefaultFilterValues = (regionOptions?: IRecord[]): IAlineaCalibrationFilterValues => {
  return {
    ...DEFAULT_FILTER_VALUES,
    rangeTime: { ...DEFAULT_FILTER_VALUES.rangeTime },
    releaseTime: { ...DEFAULT_FILTER_VALUES.releaseTime },
    games: { ...DEFAULT_FILTER_VALUES.games },
    region: getAllRegionValues(regionOptions),
    genre: { ...DEFAULT_FILTER_VALUES.genre, genres: [] },
  };
};
